import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Auto-Gift (TQ Customs)
 * ----------------------
 * Legt jeder Bestellung automatisch zwei Gratis-Goodies bei (Schlüsselanhänger +
 * Sticker-Set), sobald mindestens ein echtes Produkt im Warenkorb liegt. Die
 * Goodies werden auf Menge 1 fixiert und automatisch entfernt, sobald keine
 * echten Produkte mehr im Warenkorb sind.
 *
 * Erkennung der Goodies: über die ProductVariant-IDs unten (GOODIE_VARIANT_IDS).
 * Das Ausblenden von „Entfernen“/Mengen-Auswahl passiert server-seitig in
 * snippets/cart-products.liquid anhand von product.type == 'Goodie'.
 *
 * Die Reconcile-Logik ist idempotent: ein Event, das sie selbst auslöst, wird
 * ignoriert (detail.source === 'auto-gift'), und wenn bereits alles stimmt,
 * passiert nichts — dadurch entsteht keine Endlosschleife.
 */

// --- CONFIG: Variant-IDs der Gratis-Goodies -------------------------------
// Bei Produktwechsel hier anpassen. Optional per window.__TQ_GOODIE_VARIANT_IDS
// (Array aus numerischen IDs) in Liquid überschreibbar.
const DEFAULT_GOODIE_VARIANT_IDS = [
  57620530954620, // Schlüsselanhänger – Gratis Goodie
  57620531478908, // Sticker-Set – Gratis Goodie
];

const GOODIE_VARIANT_IDS = (
  Array.isArray(window.__TQ_GOODIE_VARIANT_IDS) && window.__TQ_GOODIE_VARIANT_IDS.length
    ? window.__TQ_GOODIE_VARIANT_IDS
    : DEFAULT_GOODIE_VARIANT_IDS
).map(Number);

const GOODIE_QTY = 1; // ein Satz pro Bestellung

/** @param {string} url */
const ensureJs = (url) => (url && url.endsWith('.js') ? url : `${url || ''}.js`);

const routes = (window.Theme && window.Theme.routes) || {};
const CART_URL = ensureJs(routes.cart_url || '/cart');
const CART_ADD_URL = ensureJs(routes.cart_add_url || '/cart/add');
const CART_UPDATE_URL = ensureJs(routes.cart_update_url || '/cart/update');

const isGoodie = (variantId) => GOODIE_VARIANT_IDS.includes(Number(variantId));

/** Alle cart-items-component Section-IDs für die Section-Rendering API. */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

class AutoGiftController {
  #busy = false;
  #pending = false;

  constructor() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);

    // Erst-Abgleich, falls der Warenkorb bereits echte Produkte (aber keine
    // Goodies) enthält – z. B. aus einer älteren Session.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#reconcile(), { once: true });
    } else {
      this.#reconcile();
    }
  }

  /** @param {CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    // Eigene Events ignorieren -> kein Loop.
    if (event?.detail?.source === 'auto-gift') return;

    // Nach Abschluss der auslösenden Operation abgleichen, damit /cart.js den
    // aktuellen Server-Stand widerspiegelt.
    const run = () => this.#reconcile();
    if (event?.promise?.then) {
      event.promise.then(run).catch(run);
    } else {
      run();
    }
  };

  async #reconcile() {
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;
    try {
      const cart = await fetch(CART_URL, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const items = Array.isArray(cart.items) ? cart.items : [];

      // Cart-Stand erfassen: Mengen der Goodies + Anzahl echter Produkte.
      let realCount = 0;
      const goodieQty = new Map(GOODIE_VARIANT_IDS.map((id) => [id, 0]));
      for (const item of items) {
        const vid = Number(item.variant_id);
        if (isGoodie(vid)) {
          goodieQty.set(vid, (goodieQty.get(vid) || 0) + item.quantity);
        } else {
          realCount += item.quantity;
        }
      }

      const anyGoodiePresent = GOODIE_VARIANT_IDS.some((id) => (goodieQty.get(id) || 0) > 0);
      const zeroUpdates = () => Object.fromEntries(GOODIE_VARIANT_IDS.map((id) => [id, 0]));
      const oneUpdates = () => Object.fromEntries(GOODIE_VARIANT_IDS.map((id) => [id, GOODIE_QTY]));

      // --- Kein echtes Produkt mehr -> alle Goodies entfernen -------------
      if (realCount === 0) {
        if (anyGoodiePresent) {
          const resp = await this.#postUpdate(zeroUpdates(), true);
          this.#dispatchRefresh(resp);
        }
        return;
      }

      // --- Mindestens ein echtes Produkt ---------------------------------
      // Position der Goodies im Warenkorb ist egal: sie werden anzeige-seitig
      // (snippets/cart-products.liquid) immer unter allen echten Produkten
      // gerendert. Hier nur Vorhandensein + Menge 1 sicherstellen.
      const missing = GOODIE_VARIANT_IDS.filter((id) => (goodieQty.get(id) || 0) === 0);
      const overQty = GOODIE_VARIANT_IDS.some((id) => (goodieQty.get(id) || 0) > GOODIE_QTY);

      if (missing.length || overQty) {
        if (missing.length) {
          await this.#postAdd(missing.map((id) => ({ id, quantity: GOODIE_QTY, properties: { _goodie: 'true' } })));
        }
        const resp = await this.#postUpdate(oneUpdates(), true);
        this.#dispatchRefresh(resp);
        return;
      }

      // Alles korrekt (vorhanden, Menge 1, zuletzt) -> nichts tun, Schleife endet.
    } catch (error) {
      console.warn('[auto-gift] reconcile failed:', error);
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        this.#reconcile();
      }
    }
  }

  /** POST /cart/add.js */
  #postAdd(items) {
    return fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
  }

  /**
   * POST /cart/update.js. Mit withSections = true kommt das Section-HTML fürs
   * Re-Render zurück.
   * @param {Record<string|number, number>} updates
   * @param {boolean} withSections
   */
  #postUpdate(updates, withSections) {
    const body = { updates };
    if (withSections) {
      body.sections = cartSectionIds().join(',');
      body.sections_url = window.location.pathname;
    }
    return fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());
  }

  /** @param {any} cartResponse Antwort von /cart/update.js (inkl. sections). */
  #dispatchRefresh(cartResponse) {
    if (!cartResponse || typeof cartResponse !== 'object') return;

    const deferred = CartLinesUpdateEvent.createPromise();
    // action 'update' statt 'add' -> löst KEIN Auto-Open des Drawers aus.
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: 'auto-gift', quantity: GOODIE_QTY }],
        promise: deferred.promise,
        detail: { source: 'auto-gift' },
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartResponse),
      detail: {
        sections: cartResponse.sections,
        items: cartResponse.items,
        itemCount: cartResponse.item_count,
        source: 'auto-gift',
        didError: false,
      },
    });
  }
}

if (!window.__tqAutoGift) {
  window.__tqAutoGift = new AutoGiftController();
}
